// Client Shell — Müşteri Katılımcı Rolü (Task 8) + M16 T19 portal üst sekmeleri (DR1/RK21).
// AppShell'in sade hali: SIDEBAR YOK; 60 px üst bar (sol logo + üst sekmeler, sağ kullanıcı adı +
// Çıkış). Üst sekmeler `role="tablist"`: Formlarım (/) · Performans Göstergeleri (/portal/kpi —
// YALNIZ `GET /portal/kpi/sets` ≥1 set döndüğünde; erişim yoksa DOM'da YOK) · Kullanıcılar
// (/portal/users — yalnız `user.adminClientIds.length > 0`). KPI içinde 44 px ikinci satır:
// Veri Girişi · Özet · İçe Aktar. ≤1024px'te yatay scroll YASAK (tek sütun akışkan layout).
//
// `--layout-shell-header`: tam-yükseklik isteyen sayfalar (FormFill, portal tablo) bu değerden
// `calc(100vh - …)` türetir — 60 px; KPI alt sekme satırı görünürken 104 px (60 + 44).
//
// 09 spec iddiaları (RK21): müşteri DOM'unda link "Dashboard"/"Müşteriler", buton "AI Üretimi
// Başlat"/"Önizleme"/"Danışman notu", metin /AI üretimi/i YOK — bu kabukta ve portal KPI
// yüzeyinde "AI" ifadesi geçmez (AI Kontrolü dahil).
//
// K8 (Finar 2026-10-23): Mahir logosunun sağında MÜŞTERİ firmasının logosu (marka etkin ve logo
// varsa; `GET /portal/branding` yalnız {clientName, logoUrl}) + firma adı. Birden çok firma adına
// çalışan kullanıcıda ad yerine yerel `<select>` (portal-store `activeClientId`); değişim portal
// KPI store'unu sıfırlar, set listesi aktif firmaya süzülerek yeniden yüklenir, ClientHome'a
// dönülür. ≤640 (`narrow`) firma adı metni gizlenir, logo küçülür; seçici kalır (dar).
//
// Kickout sözleşmesi (eng-review D7/D11): whitelist endpoint'i top-level PARTICIPATION_ENDED
// dönerse (api/client.ts merkezi maybeKickout) ClientHome'a mesajla yönlendirilir.
// CLIENT_FORBIDDEN / KPI_CLIENT_FORBIDDEN bu handler'ı hiç tetiklemez (RK22).
//
// Portal turu (2026-10-06) düzeltmeleri: P1-4 mobil (≤640) sekmeler tam genişlikte yatay
// kaydırılabilir (44 px hedef, logo küçülür, ad gizlenir); P2-6 bildirim zili — `GET /notifications`
// probu 403 dönerse zil hiç çizilmez (sessiz); P3-14 "Formlarım" sekmesine bilinçli tıklama
// (`state.explicit`) form daveti yokken KPI'ya geri sıçramaz — ClientHome boş durumu gösterir
// (sekme e2e 15b gereği her zaman görünür); P3-26 kabuk `<main>` çizmez — her sayfa kendi
// landmark'ını taşır (FormFill zaten `<main>`; iç içe iki main a11y hatasıydı).
// TODO-KPI-06 (2026-10-07): ≤1024 px'te üst/alt sekmeler daha dar (dolgu 10 px, alt sekme aralığı
// 16 px) — `compact`; logo/ad davranışı yalnız ≤640 (`narrow`) ile değişmeye devam eder.

import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../stores/auth-store";
import { routeFirmHint } from "../../lib/portal-deep-link";
import { usePortalKpiStore } from "../../stores/portal-kpi-store";
import { activeFirmName, usePortalStore } from "../../stores/portal-store";
import { apiRaw, registerKickoutHandler } from "../../api/client";
import { KPI_COPY } from "../../lib/kpi-ui";
import { useMediaQuery } from "../../lib/use-media-query";
import { NotificationBell } from "./NotificationBell";

export const CLIENT_KICKOUT_MESSAGE =
  "Form erişiminiz kapandı — danışmanınız gerekirse yeniden açabilir";

export const CLIENT_SHELL_HEADER_PX = 60;
export const CLIENT_SHELL_SUBTABS_PX = 44;

const P = KPI_COPY.portal;

function tabStyle(active: boolean, sub = false, narrow = false): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 44,
    padding: sub ? "0 4px" : narrow ? "0 10px" : "0 14px",
    marginRight: sub ? (narrow ? 16 : 24) : 4,
    flexShrink: 0,
    borderRadius: sub ? 0 : 8,
    fontSize: sub ? "0.9rem" : "0.95rem",
    fontWeight: active ? 600 : 500,
    color: active ? "var(--color-primary)" : "var(--color-text-2)",
    background:
      active && !sub ? "color-mix(in srgb, var(--color-primary) 10%, white)" : "transparent",
    borderBottom: sub ? `2px solid ${active ? "var(--color-primary)" : "transparent"}` : "none",
    textDecoration: "none",
    whiteSpace: "nowrap",
  };
}

export function ClientShell(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const location = useLocation();
  const setsStatus = usePortalKpiStore((s) => s.setsStatus);
  const setCount = usePortalKpiStore((s) => s.sets.length);
  const loadSets = usePortalKpiStore((s) => s.loadSets);
  const firms = usePortalStore((s) => s.firms);
  const firmsStatus = usePortalStore((s) => s.firmsStatus);
  const activeClientId = usePortalStore((s) => s.activeClientId);
  const branding = usePortalStore((s) => s.branding);
  const firmName = usePortalStore(activeFirmName);
  const loadFirms = usePortalStore((s) => s.load);
  const setActiveClient = usePortalStore((s) => s.setActiveClient);
  const followProject = usePortalStore((s) => s.followProject);
  const narrow = useMediaQuery("(max-width: 640px)");
  const compact = useMediaQuery("(max-width: 1024px)");
  // P2-6 — bildirim ucu müşteriye açık mı? 403 (whitelist dışı) → zil sessizce gizli.
  const [bell, setBell] = useState<"probe" | "on" | "off">("probe");

  useEffect(() => {
    registerKickoutHandler(() => {
      void navigate("/", { replace: true, state: { kickoutMessage: CLIENT_KICKOUT_MESSAGE } });
    });
    return () => registerKickoutHandler(null);
  }, [navigate]);

  // K8 — firma listesi + aktif firma (rota projesinin firması tercih edilir).
  useEffect(() => {
    if (!user) return;
    const m = /^\/projects\/([0-9a-f-]{36})/i.exec(location.pathname);
    void loadFirms(user.id, { hintProjectId: m?.[1] ?? null });
    // yalnız kullanıcı değişince — rota değişimi firma seçimini değiştirmez
  }, [user?.id, loadFirms]);

  // TODO-KPI-57/10 — uygulama içi gezinmede (ör. bildirimden başka firmanın formuna) üst bar firması
  // rota projesinin firmasını izler; `hintProjectId` yalnız ilk yüklemeye bakıyordu.
  useEffect(() => {
    if (!user || firmsStatus !== "ready") return;
    const hint = routeFirmHint(location.pathname);
    if (hint) void followProject(hint.projectId);
  }, [user?.id, firmsStatus, location.pathname, followProject]);

  // DR1 — set erişimi: sekme görünürlüğü + ClientHome landing (DR2) aynı store'dan okunur.
  // K8: firma listesi bitene kadar beklenir (aksi halde süzülmemiş liste landing'i yanıltırdı);
  // liste alınamadıysa süzmeden yüklenir (fail-open).
  useEffect(() => {
    if (!user) return;
    if (firmsStatus !== "ready" && firmsStatus !== "error") return;
    void loadSets(user.id, { clientId: firmsStatus === "ready" ? activeClientId : null });
  }, [user?.id, loadSets, firmsStatus, activeClientId]);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    apiRaw<unknown>("/notifications?pageSize=10")
      .then((r) => {
        if (alive) setBell(r.ok ? "on" : "off");
      })
      .catch(() => {
        if (alive) setBell("off");
      });
    return () => {
      alive = false;
    };
  }, [user?.id]);

  if (!user) return <></>;

  const hasKpi = setsStatus === "ready" && setCount > 0;
  const isAdmin = (user.adminClientIds ?? []).length > 0;
  const inKpi = location.pathname.startsWith("/portal/kpi");
  // güvenlik/UX taraması 2026-10-08 (U-M6): role="tab" NavLink'in aria-current'ını gölgeler; e2e 15b
  // `getByRole("tablist"/"tab")` kullandığı için roller kalır, HER sekme aria-selected taşır.
  const inForms = location.pathname === "/";
  const inUsers = location.pathname.startsWith("/portal/users");
  const subActive = (to: string): boolean => location.pathname.startsWith(to);
  const headerPx = CLIENT_SHELL_HEADER_PX + (inKpi && hasKpi ? CLIENT_SHELL_SUBTABS_PX : 0);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={
        {
          background: "var(--color-bg)",
          "--layout-shell-header": `${headerPx.toString()}px`,
        } as React.CSSProperties
      }
    >
      <header
        style={{
          height: CLIENT_SHELL_HEADER_PX,
          flexShrink: 0,
          background: "var(--color-surface)",
          borderBottom: "1px solid var(--color-border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: narrow ? "0 12px" : "0 20px",
          gap: narrow ? 8 : 12,
        }}
      >
        <div
          className={narrow ? "flex items-center gap-3" : "flex items-center gap-5"}
          style={{ minWidth: 0, flex: 1 }}
        >
          <img
            src="/Mahir_logo.png"
            alt="Mahir AI"
            style={{ height: narrow ? 22 : 30, width: "auto", display: "block", flexShrink: 0 }}
          />
          <FirmBadge
            narrow={narrow}
            firms={firms}
            activeClientId={activeClientId}
            name={firmName}
            logoUrl={branding?.logoUrl ?? null}
            onSelect={(id) => {
              void setActiveClient(id);
              void navigate("/", { replace: false });
            }}
          />
          <nav
            role="tablist"
            aria-label="Portal"
            className="flex items-center"
            style={{ overflowX: "auto", minWidth: 0, flex: 1, scrollbarWidth: "none" }}
          >
            <NavLink
              to="/"
              end
              role="tab"
              aria-selected={inForms}
              state={{ explicit: true }}
              style={({ isActive }) => tabStyle(isActive, false, compact)}
            >
              {P.tabs.forms}
            </NavLink>
            {hasKpi ? (
              <NavLink
                to="/portal/kpi/tablo"
                role="tab"
                data-testid="portal-tab-kpi"
                style={() => tabStyle(inKpi, false, compact)}
                aria-selected={inKpi}
              >
                {P.tabs.kpi}
              </NavLink>
            ) : null}
            {isAdmin ? (
              <NavLink
                to="/portal/users"
                role="tab"
                aria-selected={inUsers}
                data-testid="portal-tab-users"
                style={({ isActive }) => tabStyle(isActive, false, compact)}
              >
                {P.tabs.users}
              </NavLink>
            ) : null}
          </nav>
        </div>
        <div className="flex items-center gap-3" style={{ minWidth: 0, flexShrink: 0 }}>
          {bell === "on" ? <NotificationBell /> : null}
          {!narrow ? (
            <span
              className="truncate"
              style={{
                fontSize: "0.85rem",
                color: "var(--color-text-2)",
                fontFamily: "var(--font-sans)",
                maxWidth: 220,
              }}
            >
              {user.fullName}
            </span>
          ) : null}
          <button
            aria-label={narrow ? `Çıkış (${user.fullName})` : undefined}
            onClick={() => void logout()}
            style={{
              flexShrink: 0,
              fontSize: "0.8rem",
              color: "var(--color-text-3)",
              border: "1px solid var(--color-border)",
              borderRadius: 8,
              padding: narrow ? "6px 8px" : "6px 12px",
              minHeight: 36,
              background: "var(--color-surface)",
              cursor: "pointer",
            }}
          >
            Çıkış
          </button>
        </div>
      </header>

      {inKpi && hasKpi ? (
        <nav
          role="tablist"
          aria-label="Performans göstergeleri"
          className="flex items-center"
          style={{
            height: CLIENT_SHELL_SUBTABS_PX,
            flexShrink: 0,
            padding: narrow ? "0 12px" : "0 20px",
            background: "var(--color-surface)",
            borderBottom: "1px solid var(--color-border)",
            overflowX: "auto",
            minWidth: 0,
            scrollbarWidth: "none",
          }}
        >
          {(
            [
              ["/portal/kpi/tablo", P.subTabs.tablo],
              ["/portal/kpi/ozet", P.subTabs.ozet],
              ["/portal/kpi/import", P.subTabs.import],
            ] as const
          ).map(([to, label]) => (
            <NavLink
              key={to}
              to={to}
              role="tab"
              aria-selected={subActive(to)}
              style={({ isActive }) => tabStyle(isActive, true, compact)}
            >
              {label}
            </NavLink>
          ))}
        </nav>
      ) : null}

      {/* landmark sayfada (P3-26) — FormFill/portal sayfaları kendi <main>'ini çizer */}
      <div className="flex-1 min-w-0">
        <Outlet />
      </div>
    </div>
  );
}

/** K8 — müşteri firması logosu + adı / çok firmalıda seçici. Firma yoksa hiçbir şey çizmez. */
function FirmBadge(props: {
  narrow: boolean;
  firms: { clientId: string; clientName: string }[];
  activeClientId: string | null;
  name: string | null;
  logoUrl: string | null;
  onSelect: (clientId: string) => void;
}): JSX.Element | null {
  const { narrow, firms, activeClientId, name, logoUrl, onSelect } = props;
  if (!activeClientId || !name) return null;
  const F = P.firmSwitcher;
  return (
    <div
      data-testid="portal-firm"
      className="flex items-center"
      style={{
        gap: 8,
        minWidth: 0,
        flexShrink: 1,
        maxWidth: narrow ? 140 : 280,
        paddingLeft: narrow ? 8 : 16,
        borderLeft: "1px solid var(--color-border)",
      }}
    >
      {logoUrl ? (
        <img
          data-testid="portal-firm-logo"
          src={logoUrl}
          alt={F.logoAlt(name)}
          style={{
            maxHeight: narrow ? 22 : 32,
            maxWidth: narrow ? 60 : 96,
            width: "auto",
            objectFit: "contain",
            display: "block",
            flexShrink: 0,
          }}
        />
      ) : null}
      {firms.length > 1 ? (
        <select
          aria-label={F.label}
          title={F.hint}
          data-testid="portal-firm-select"
          value={activeClientId}
          onChange={(e) => onSelect(e.target.value)}
          style={{
            minWidth: 0,
            maxWidth: "100%",
            minHeight: 36,
            fontSize: "0.85rem",
            fontFamily: "var(--font-sans)",
            color: "var(--color-text)",
            border: "1px solid var(--color-border)",
            borderRadius: 8,
            padding: "4px 8px",
            background: "var(--color-surface)",
            textOverflow: "ellipsis",
          }}
        >
          {firms.map((f) => (
            <option key={f.clientId} value={f.clientId}>
              {f.clientName}
            </option>
          ))}
        </select>
      ) : !narrow ? (
        <span
          data-testid="portal-firm-name"
          className="truncate"
          style={{ fontSize: "0.9rem", fontWeight: 600, color: "var(--color-text)" }}
        >
          {name}
        </span>
      ) : null}
    </div>
  );
}
